import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '@ajat/design-system/Header';
import SchoolSelector from './SchoolSelector';
import EditOrderLogin from '@ajat/design-system/EditOrderLogin';
import { useCart } from '../../../../context/CartContext';
import useHeaderMenuIntent from '../../../../hooks/useHeaderMenuIntent';
import useMenuItemVisibility from '../../../../hooks/useMenuItemVisibility';
import useBrandSettings from '../../../../hooks/useBrandSettings';
import useBrandSeo from '../../../../hooks/useBrandSeo';
import useDocumentLang from '../../../../hooks/useDocumentLang';
import { readFreshSelection } from '../../../../services/schoolSelectionTtl';
import { shopEntry, schoolShopPath } from '../../../../services/menuDestination';
import { loginToOrder } from '../../Common/EditOrder/ordersApi';

/* ══════════════════════════════════════════════════════════════════════════
 * CLS-FI — Common Header (TopBar + Header)
 * Single source of truth for header configuration across all CLS-FI pages.
 * Behaviour = CLS-DK (2026-10-14 alignment): mandatory identification mode,
 * prefill, stay-on-page after selection, menu intents, visibility rules.
 * FI has no BookMeeting / create-school / logo generator — "school not found"
 * leads to the contact page.
 * ══════════════════════════════════════════════════════════════════════════ */

// The mobile promo card advertises the cap wizard (external) — not tied to a
// merch menu item, so it never hides.
const mobileSettings = {
  mobileTitle: 'Valikko',
  mobilePromo: {
    cardType: 'button',
    image: 'https://content-fi.clseifert.com/wp-content/uploads/2021/01/resizable_e5b0d077-ea32-4b8f-9282-40cedf6527f8-1-e1762695150542.jpg',
    imageAlt: 'Suunnittele ylioppilaslakki',
    label: 'SUUNNITTELE YLIOPPILASLAKKISI',
    href: 'https://clseifert.com/fi/wizard',
  },
};

// Built per instance so "Haalarit & muut" (menu_1) and "Vaatteet" (menu_2)
// resolve to the customer's mapped school shop via onMenuItemClick
// (useHeaderMenuIntent). `menuKey` tags the two fixed entries so the header
// can hide one under the "Disable Open Shop" flag (useMenuItemVisibility);
// untagged entries (the cap-site links) never hide.
const buildNavItems = (onMenuItemClick) => [
  {
    label: 'Ylioppilaslakit',
    href: 'https://clseifert.com/fi/wizard',
    submenu: {
      watermark: 'Ylioppilaslakit',
      columns: [
        { type: 'card', cardType: 'button', image: 'https://content-fi.clseifert.com/wp-content/uploads/2021/01/resizable_e5b0d077-ea32-4b8f-9282-40cedf6527f8-1-e1762695150542.jpg', imageAlt: 'Suunnittele ylioppilaslakki', label: 'SUUNNITTELE YLIOPPILASLAKKISI', href: 'https://clseifert.com/fi/wizard' },
        { type: 'card', cardType: 'link', image: 'https://content-fi.clseifert.com/wp-content/uploads/2021/01/sovitussetti-e1762696203296.png', imageAlt: 'Tilaa sovitussetti', label: 'Tilaa sovitussetti', href: 'https://clseifert.com/fi/tilaa-sovitussetti' },
        { type: 'card', cardType: 'link', image: 'https://content-fi.clseifert.com/wp-content/uploads/2021/01/koulu-e1762696407230.png', imageAlt: 'Koulun yhteistilaus', label: 'Koulun yhteistilaus', href: 'https://clseifert.com/fi/koulun-yhteistilaus' },
        {
          type: 'links',
          items: [
            { label: 'Kustomoinnit', href: 'https://clseifert.com/fi/kustomoinnit' },
            { label: 'Äidinlyyra', href: 'https://clseifert.com/fi/aidinlyyra' },
            { label: 'Yo-lakin oikea koko', href: 'https://clseifert.com/fi/yo-lakin-oikea-koko' },
            { label: 'Ohjevideot', href: 'https://clseifert.com/fi/ohjevideot' },
            { label: 'Studentmössor', href: 'https://clseifert.com/fi/studentmossan' },
          ],
        },
      ],
    },
  },
  {
    label: 'Haalarit & muut',
    menuKey: 'menu_1',
    href: '#',
    onClick: (e) => { e.preventDefault(); onMenuItemClick('menu_1'); },
  },
  {
    label: 'Vaatteet',
    menuKey: 'menu_2',
    href: '#',
    onClick: (e) => { e.preventDefault(); onMenuItemClick('menu_2'); },
  },
  {
    label: 'Oheistuotteet',
    href: '#',
    submenu: {
      watermark: 'Oheistuotteet',
      columns: [
        { type: 'card', cardType: 'button', image: 'https://content-fi.clseifert.com/wp-content/uploads/2021/01/accessories-01.jpg', imageAlt: 'Kaikki Oheistuotteet', label: 'Kaikki Oheistuotteet', href: 'https://clseifert.com/fi/oheistuotteet' },
        { type: 'card', cardType: 'link', image: 'https://content-fi.clseifert.com/wp-content/uploads/2021/01/amis-3.png', imageAlt: 'Suunnittele Ammattilakki', label: 'Suunnittele Ammattilakki', href: 'https://clseifert.com/fi/wizard' },
        { type: 'card', cardType: 'link', image: 'https://content-fi.clseifert.com/wp-content/uploads/2025/08/2026-cls-fi-cap-vocational-basic-uni-scaled-e1755496671671.jpg', imageAlt: 'Suunnittele AMK-lakki', label: 'Suunnittele AMK-lakki', href: 'https://clseifert.com/fi/wizard' },
        { type: 'card', cardType: 'link', image: 'https://content-se.clseifert.com/wp-content/uploads/2021/01/inside-3-1.webp', imageAlt: 'Töihin meille', label: 'Töihin meille', href: 'https://clseifert.com/fi/avoimet-tyopaikat' },
      ],
    },
  },
  {
    label: 'FAQ',
    href: '/clothing/cls-fi/faq',
  },
];

const topBarNotice = '+ 75 000 ASIAKASTA VUOSITTAIN';

const topBarLinks = [
  { label: 'FAQ', href: '/clothing/cls-fi/faq' },
];

/* The product pages mount this Header twice (desktop + mobile wrappers, one of
 * them display:none) and may fire the open-school-selector event while only the
 * hidden one exists (loading branch). A hidden instance parks the request here;
 * the next VISIBLE instance to mount consumes it, so the popup always appears
 * exactly once, in the header the customer can see. */
let parkedSelectorOpen = null;
let lastSelectorOpenAt = 0;

const CLSFIHeader = ({
  showTopBar = false,
  hideOnScroll = true,
  onSchoolClick,
  schoolSelectorLockScroll = true,
  ...headerProps
}) => {
  const navigate = useNavigate();
  // Sibling routes that share one route element (e.g. /shop/:menuSlug) keep
  // this Header mounted, so the DS component needs a navigation signal to
  // close its open menus. location.key changes on EVERY history entry —
  // including a menu click that re-navigates to the page you are already on.
  const location = useLocation();
  const cart = useCart('clsfi');
  const { showSchoolSelector } = useBrandSettings('clsfi');
  useBrandSeo('clsfi');
  useDocumentLang('fi');   // <html lang> — public/index.html says "da"
  const [isSchoolSelectorOpen, setIsSchoolSelectorOpen] = useState(false);
  const [isEditOrderLoginOpen, setIsEditOrderLoginOpen] = useState(false);
  const [pendingNavigate, setPendingNavigate] = useState(null);
  // Mandatory identification mode (product pages): popup is non-dismissible
  // and, when the picked school matches `staySchool` (or it is '*'), the
  // customer STAYS on the page they came from after selecting.
  const [mandatorySelection, setMandatorySelection] = useState(false);
  // School + shop known from the URL that opened the popup (see
  // useForceSchoolSelection `prefill`); cleared when the popup closes.
  const [selectorPrefill, setSelectorPrefill] = useState(null);
  const staySchoolRef = useRef(null);
  // The product pages mount this Header twice (desktop + mobile wrappers, one of
  // them display:none). Only the VISIBLE instance may open the school popup —
  // the hidden one would render an invisible overlay that still locks the page.
  const rootRef = useRef(null);
  const isVisibleInstance = () => !rootRef.current || rootRef.current.getClientRects().length > 0;

  // Menu-item → mapped-shop resolution for the two fixed header items.
  const {
    onMenuItemClick,
    readIntentFromEvent,
    clearIntent,
    preferredSeasonResolver,
    showPreferredSeason,
    navigateForIntentAfterSelect,
  } = useHeaderMenuIntent({ brandSlug: 'clsfi', feSlug: 'cls-fi' });

  // When the open shop is disabled and the customer's school has no open shop
  // of that type, a menu-tied entry is neutralized: an entry with a submenu
  // keeps its section (minus the buy card), a plain entry is dropped. Untagged
  // entries (the cap-site links) always stay whole.
  const { isVisible, isHidden, isUnmappedForSchool } = useMenuItemVisibility('clsfi');
  const navItems = useMemo(
    () => buildNavItems(onMenuItemClick).flatMap((item) => {
      // Admin-hidden, or unmapped for the customer's school (no shop / group
      // linked to it) → gone, submenu included.
      if (item.menuKey && (isHidden(item.menuKey) || isUnmappedForSchool(item.menuKey))) return [];
      if (!item.menuKey || isVisible(item.menuKey)) return [item];
      if (!item.submenu) return [];
      return [{
        ...item,
        onClick: (e) => e.preventDefault(),
        submenu: {
          ...item.submenu,
          columns: item.submenu.columns.filter((c) => !(c.type === 'card' && c.cardType === 'button')),
        },
      }];
    }),
    [onMenuItemClick, isVisible, isHidden, isUnmappedForSchool],
  );

  // Show the selected school's name on the header school button (read fresh
  // each render — selecting/clearing re-renders via the selector state).
  const storedSchoolName = readFreshSelection('clsfi_school_selection')?.school?.name || null;

  /* Build minicart items from cart context */
  const priceSuffix = ' €';
  const cartItems = [...cart.products, ...cart.packages].map((item) => {
    const opts = item.options || {};
    const details = [];
    if (opts.color) details.push(opts.color + (opts.size ? ` ${opts.size}` : ''));
    else if (opts.size) details.push(opts.size);
    if (opts.addons) {
      if (opts.addons.navnetryk) details.push(`Nimipainatus: ${opts.addons.navnetryk}`);
      if (opts.addons.lilleBrysttryk) details.push('Pieni rintapainatus');
    }
    // Bonus products (server-appended, free) stay out of the minicart count.
    const visibleIncluded = (item.includedProducts || []).filter((ip) => !ip?.bonus);
    if (visibleIncluded.length) details.push(`${visibleIncluded.length} tuotetta sisältyy`);
    return {
      id: item.id,
      name: item.name,
      image: opts.image || item.image,
      details,
      quantity: item.quantity,
      price: `${Math.round(item.lineTotal)}${priceSuffix}`,
    };
  });

  useEffect(() => {
    // Selector disabled for the brand → never open the popup from the event.
    if (!showSchoolSelector) return undefined;
    const applyOpen = (e) => {
      setIsSchoolSelectorOpen(true);
      setPendingNavigate(e.detail?.navigateTo ?? null);
      setMandatorySelection(Boolean(e.detail?.mandatory));
      setSelectorPrefill(e.detail?.prefill ?? null);
      staySchoolRef.current = e.detail?.mandatory ? (e.detail?.staySchool ?? null) : null;
      readIntentFromEvent(e);
      lastSelectorOpenAt = Date.now();
      parkedSelectorOpen = null;
    };
    const handleOpen = (e) => {
      if (!isVisibleInstance()) { parkedSelectorOpen = { detail: e.detail, at: Date.now() }; return; }
      applyOpen(e);
    };
    // A request parked by a hidden instance before this (visible) one mounted.
    if (parkedSelectorOpen && parkedSelectorOpen.at > lastSelectorOpenAt
      && Date.now() - parkedSelectorOpen.at < 15000 && isVisibleInstance()) {
      applyOpen(new CustomEvent('open-school-selector', { detail: parkedSelectorOpen.detail }));
    }
    document.addEventListener('open-school-selector', handleOpen);
    return () => {
      document.removeEventListener('open-school-selector', handleOpen);
    };
  }, [showSchoolSelector, readIntentFromEvent]);

  const handleSchoolClick = () => {
    if (!showSchoolSelector) return;
    setIsSchoolSelectorOpen(true);
    setPendingNavigate(null);
    setMandatorySelection(false);
    staySchoolRef.current = null;
    clearIntent();
    if (onSchoolClick) onSchoolClick();
  };

  const handleUserClick = () => {
    setIsEditOrderLoginOpen(true);
  };

  const handleNavigateAfterSelect = (selection) => {
    if (pendingNavigate) {
      const target = pendingNavigate;
      setPendingNavigate(null);
      navigate(target);
    } else if (location.pathname === '/clothing/cls-fi') {
      // Plain login from the landing's header button: stay on the landing —
      // the header shows the school, and every card / CTA click now resolves
      // into the customer's own shop → sub-shop → item.
    } else if (selection?.school?.slug) {
      // Elsewhere: land in the chosen shop's entry page — the first group of
      // the shop the customer picked (never a grouped shop's root), else the
      // shop root, else the whole-school page.
      const { shop, group } = shopEntry(selection.school, null, selection.season ?? null);
      navigate(shop ? schoolShopPath('cls-fi', selection.school.slug, shop, group) : `/clothing/cls-fi/school-shop/${selection.school.slug}`);
    } else {
      navigate('/clothing/cls-fi/school-shop');
    }
  };

  return (
    <>
      <span ref={rootRef} aria-hidden="true" style={{ display: 'block', width: 0, height: 0 }} />
      <Header
        brand="CLS"
        subBrand="main"
        mode="default"
        logoHref="/clothing/cls-fi"
        onLogoClick={(e) => { e.preventDefault(); navigate('/clothing/cls-fi'); }}
        schoolLabel={storedSchoolName || 'VALITSE KOULU'}
        showTopBar={showTopBar}
        topBarNotice={topBarNotice}
        topBarLinks={topBarLinks}
        hideOnScroll={hideOnScroll}
        navItems={navItems}
        mobileSettings={mobileSettings}
        routeKey={location.key}
        onSchoolClick={handleSchoolClick}
        showSchoolButton={showSchoolSelector}
        cartCount={cart.itemCount}
        cartItems={cartItems}
        cartTotal={`${Math.round(cart.total)} €`}
        cartItemsLabel="Tuotteet"
        cartTotalLabel="Yhteensä"
        cartCtaLabel="Kassalle"
        onCartCtaClick={() => navigate('/clothing/cls-fi/shopping-cart')}
        showUserButton
        orderLabel="Muokkaa tilausta"
        onUserClick={handleUserClick}
        {...headerProps}
      />
      <SchoolSelector
        isOpen={showSchoolSelector && isSchoolSelectorOpen}
        dismissible={!mandatorySelection}
        onClose={() => {
          setIsSchoolSelectorOpen(false);
          setPendingNavigate(null);
          setMandatorySelection(false);
          setSelectorPrefill(null);
          // Dismissing just closes the popup; drop the pending menu intent.
          clearIntent();
        }}
        onSelect={(selection) => {
          setIsSchoolSelectorOpen(false);
          setMandatorySelection(false);
          setSelectorPrefill(null);
          // Mandatory identification from a product page: stay on that page
          // when the picked school matches (or staySchool is '*') — the
          // customer proceeds with the product they came for.
          const stay = staySchoolRef.current;
          staySchoolRef.current = null;
          if (stay && (stay === '*' || selection?.school?.slug === stay)) return;
          // A pending menu intent navigates to the mapped shop (or fallback).
          if (navigateForIntentAfterSelect(selection)) return;
          handleNavigateAfterSelect(selection);
        }}
        prefill={selectorPrefill}
        preferredSeasonResolver={preferredSeasonResolver}
        showPreferredSeason={showPreferredSeason}
        onNotFoundClick={() => {
          setPendingNavigate(null);
          navigate('/clothing/cls-fi/contact');
        }}
        lockScroll={schoolSelectorLockScroll}
        notFoundTitle="Etkö löydä kouluasi?"
      />
      <EditOrderLogin
        onLogin={loginToOrder}
        isOpen={isEditOrderLoginOpen}
        onClose={() => setIsEditOrderLoginOpen(false)}
        apiBrandSlug="clsfi"
        onSuccess={({ orderNumber, token, order }) => {
          setIsEditOrderLoginOpen(false);
          navigate(`/clothing/cls-fi/edit-order/${orderNumber}`, { state: { token, order } });
        }}
        notFoundMessage="Emme löytäneet tilausta annetulla sähköpostilla ja tilausnumerolla."
        title="Muokkaa tilausta"
        infoText="Löydät tilausnumerosi vahvistussähköpostista, jonka olemme lähettäneet sinulle."
        emailLabel="Sähköposti"
        orderNumberLabel="Tilausnumero"
        submitLabel="Jatka"
        emailErrorMessage="Anna kelvollinen sähköpostiosoite"
        orderNumberErrorMessage="Anna kelvollinen tilausnumero"
      />
    </>
  );
};

export default CLSFIHeader;
